/**
 * Store de filtre faceted (pattern Float — cf. analyse/float-filter-analysis.md).
 *
 * Partie sans React (testable sous Node) ; le hook `useFilterStore` est dans
 * `hooks/useFilterStore.js`, qui réexporte ce module.
 *
 * Un store **par scope** ({ schedule | affaires | persons | logtime | timeoffs }),
 * persisté en localStorage. Les composants consommateurs partagent l'état
 * via `useSyncExternalStore` : modifier le filtre dans la FilterBar
 * re-rendre immédiatement le contenu de la vue (et inversement).
 *
 * État interne : liste plate de `FilterCondition` avec id local (uuid) pour
 * permettre l'édition/suppression individuelle. À la sérialisation API, on
 * strippe l'id (cf. `toFilterQuery`) pour respecter le schema backend.
 *
 *   FilterCondition (UI)  ⇒ { id, dimension, operator: 'is'|'is_not', values: [...] }
 *   FilterCondition (API) ⇒ { dimension, operator, values }
 *
 * Pas de Zustand : le projet n'a pas cette dépendance et un store maison
 * couvre les besoins (1 fichier, 80 lignes).
 */

const LS_PREFIX = 'synolog_filter_';

const stores = new Map();

function loadFromStorage(scope) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + scope);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveToStorage(scope, conditions) {
  try {
    if (!conditions || conditions.length === 0) {
      localStorage.removeItem(LS_PREFIX + scope);
    } else {
      localStorage.setItem(LS_PREFIX + scope, JSON.stringify(conditions));
    }
  } catch {
    // localStorage indisponible (mode privé) — l'état reste en mémoire.
  }
}

export function newConditionId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

export function getStore(scope) {
  let store = stores.get(scope);
  if (!store) {
    let state = loadFromStorage(scope);
    // Ticket support #15 — surcharge de session (lien profond) : tant qu'elle
    // est active, l'état vit en mémoire seulement, le filtre habituel reste
    // intact en localStorage. Vider le filtre la lève et restaure ce dernier.
    let session = false;
    const listeners = new Set();
    const notify = () => listeners.forEach((cb) => cb());
    store = {
      getSnapshot: () => state,
      subscribe: (cb) => {
        listeners.add(cb);
        return () => listeners.delete(cb);
      },
      isSession: () => session,
      setState: (next) => {
        if (session) {
          if (!next || next.length === 0) {
            session = false;
            state = loadFromStorage(scope);
          } else {
            state = next;
          }
        } else {
          state = next;
          saveToStorage(scope, next);
        }
        notify();
      },
      startSession: (next) => {
        session = true;
        state = next;
        notify();
      },
      endSession: () => {
        session = false;
      },
    };
    stores.set(scope, store);
  }
  return store;
}

/** Accès hors React au store d'un scope (tests, liens profonds). */
export function getFilterStore(scope) {
  return getStore(scope);
}

/**
 * Pose un filtre **pour la session de l'onglet** sans toucher au filtre
 * mémorisé (ticket support #15). Sans condition, ne fait rien.
 */
export function applySessionFilter(scope, conditions) {
  const valid = (conditions ?? []).filter(Boolean);
  if (valid.length === 0) return;
  getStore(scope).startSession(valid.map((c) => ({ ...c, id: newConditionId() })));
}

/**
 * Réinitialise TOUS les filtres facettés (tous scopes), en mémoire ET en
 * localStorage. À appeler lors d'un changement d'identité (début/fin
 * d'impersonation) : sinon un filtre posé sous une identité (ex. « Personne est
 * X ») persiste dans la session empruntée et fausse l'affichage.
 */
export function resetAllFilters() {
  // Stores déjà instanciés : setState([]) purge le localStorage + notifie les
  // abonnés (FilterBar / vues) pour un re-render immédiat. Une surcharge de
  // session (lien profond) est levée d'abord, sinon `setState([])` restaurerait
  // le filtre persisté au lieu de le purger.
  for (const store of stores.values()) {
    store.endSession();
    store.setState([]);
  }
  // Scopes persistés mais pas encore instanciés en mémoire.
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LS_PREFIX)) toRemove.push(key);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage indisponible — ignore.
  }
}

/**
 * Convertit l'état UI (avec `id` local) en payload API `FilterQuery`.
 * Retourne `null` si vide (pour que le caller puisse skip le param).
 */
export function toFilterQuery(conditions) {
  if (!conditions || conditions.length === 0) return null;
  return {
    conditions: conditions.map(({ dimension, operator, values }) => ({
      dimension,
      operator,
      values,
    })),
  };
}

/**
 * Vrai si le filtre porte sur autre chose que « Moi » (raccourci `person is
 * [monPersonId]` de la FilterBar). Les vues Planning et Saisie des temps
 * basculent alors sur le périmètre « Tout le monde » : filtrer une équipe,
 * une affaire ou un client sur « Mes temps » / « Mon équipe » ne montrerait
 * que l'intersection, presque toujours vide.
 */
export function hasNonSelfFilter(conditions, myPersonId) {
  return (conditions ?? []).some(
    (c) =>
      !(
        c.dimension === 'person' &&
        c.operator === 'is' &&
        c.values?.length === 1 &&
        myPersonId != null &&
        String(c.values[0]) === String(myPersonId)
      ),
  );
}
